import { getAllQueryService } from "@/service/feedback.service";
import { updateSubmissionStatusService } from "@/service/submission.service";
import { selectUser, selectUserRole } from "@/store/slices/authSlice";
import { FeedbackQuery, QueryDetail } from "@/types/feedback.model";
import { ROLES } from "@/utils/constants";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import CreateQuery from "./CreateQuery";
import { QueryCard } from "./QueryCard";
import QueryFeedbackForm from "./QueryFeedbackForm";
import { markNotificationAsReadAction } from "@/store/slices/courseSlice";
import { AppDispatch } from "@/store";

function Queries({
  selectedLessonId,
  selectedUnitId,
  selectedStudentId,
  selectedAssignmentType,
  recordType = "course",
}: {
  selectedLessonId?: string;
  selectedUnitId?: string;
  selectedStudentId?: string;
  selectedAssignmentType?: string;
  recordType?: "course" | "class";
}) {
  const { id } = useParams<{ id: string }>();
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const dispatch = useDispatch<AppDispatch>();

  const processedCardRef = useRef<string | null>(null);
  const location = useLocation();

  const [queries, setQueries] = useState<QueryDetail[]>([]);

  const [loading, setLoading] = useState(false);

  const [expandedSubmission, setExpandedSubmission] = useState<string[]>([]);

  const toggleSubmission = async (queryId: string) => {
    if (expandedSubmission.includes(queryId)) {
      setExpandedSubmission(
        expandedSubmission.filter((submissionId) => submissionId !== queryId)
      );
    } else {
      setExpandedSubmission([...expandedSubmission, queryId]);

      try {
        await updateSubmissionStatusService({ queryId: queryId });
        dispatch(
          // @ts-ignore
          markNotificationAsReadAction({
            query_id: queryId,
            student_id: selectedStudentId || user?.student_user_id,
            gradeId: id,
            viewType: recordType,
          })
        );
      } catch (error) {
        console.error("Failed to update submission status:", error);
      }
    }
  };

  const fetchQueries = async () => {
    try {
      setLoading(true);
      const params: FeedbackQuery = {
        page: 1,
        pageSize: 50,
        studentId: selectedStudentId || user?.student_user_id,
        recordType,
        ...(recordType === "class"
          ? { classId: id as string }
          : { gradeId: id as string }),
      };

      if (selectedAssignmentType === "unit") {
        params.lessonId = selectedLessonId || "";
        params.unitId = selectedUnitId || "";
      }

      const {
        data: { data, pageInfo },
      } = await getAllQueryService(params);

      setQueries(data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setQueries([]);
      }

      console.error("Error fetching assignments:", error);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch course details when component mounts or id changes
  useEffect(() => {
    if (
      id &&
      selectedAssignmentType &&
      (selectedStudentId || user?.student_user_id) &&
      (selectedAssignmentType === "general" ||
        (selectedAssignmentType === "unit" &&
          selectedLessonId &&
          selectedUnitId))
    ) {
      fetchQueries();
    }

    // Cleanup function to reset current course when component unmounts
  }, [
    id,
    recordType,
    selectedAssignmentType,
    selectedLessonId,
    selectedUnitId,
    selectedStudentId,
    user,
  ]);

  useEffect(() => {
    // Only process if we have a selectedCardId and it's different from what we've already processed
    if (
      location.state?.selectedTab === "feedback" &&
      location.state?.selectedCardId &&
      processedCardRef.current !== location.state.selectedCardId
    ) {
      processedCardRef.current = location.state.selectedCardId;
      toggleSubmission(location.state.selectedCardId);
    }
  }, [location.state?.selectedCardId, location.state?.selectedTab]);

  return (
    <>
      {userRole?.code === ROLES.STUDENT && (
        <>
          <CreateQuery
            onRefresh={() => fetchQueries()}
            recordType={recordType}
            selectedAssignmentType={selectedAssignmentType}
            selectedStudentId={selectedStudentId}
            selectedLessonId={selectedLessonId || ""}
            selectedUnitId={selectedUnitId || ""}
          />
          <div className="my-4 border-t border-[2px] border-[#B7CBE6]"></div>
        </>
      )}

      <div className="w-full bg-white py-[12px] rounded-xl overflow-hidden">
        <div className="flex items-center px-[24px] justify-between p-2 border-b border-[#EEEEEE] flex-wrap sm:flex-nowrap">
          <h2 className="text-xl font-semibold text-[#1F1F1F]">
            List of Submission & Feedbacks
          </h2>
        </div>

        {/*  h-[calc(100vh-370px)] overflow-y-auto */}
        <div className="px-[24px] py-[25px] lg:h-[55rem] h-[45rem] overflow-y-auto">
          {!loading ? (
            <>
              {queries.length > 0 ? (
                <>
                  {queries.map((query, idx) => (
                    <div key={idx} className="mb-[6px]">
                      <div
                        className={`flex h-[50px] justify-between items-center px-[12px] sm:px-[24px] py-[13px] rounded-xl cursor-pointer w-full ${
                          expandedSubmission.includes(query?.id as string)
                            ? "bg-[#DEEAF7]"
                            : "bg-white border border-[#EEEEEE]"
                        }`}
                        onClick={() => toggleSubmission(query?.id as string)}
                      >
                        <h3 className="text-xl font-semibold text-[#1F1F1F]">
                          Submission {idx + 1}
                        </h3>
                        <div className="flex items-center gap-2 sm:gap-3.5 flex-shrink-0">
                          {query.status === "NEW" && (
                            <button className="w-[98px] h-[32px] bg-white text-[#5C88C5] border border-[#5C88C5] rounded-full text-[16px] font-medium">
                              New
                            </button>
                          )}
                          {expandedSubmission.includes(query?.id as string) ? (
                            <ChevronUp className="w-6 h-6 text-[#1C1B1F]" />
                          ) : (
                            <ChevronDown className="w-6 h-6 text-[#1C1B1F]" />
                          )}
                        </div>
                      </div>

                      {expandedSubmission.includes(query?.id as string) && (
                        <>
                          <div className="p-3 sm:p-6 border-x border-b rounded-xl border-[#EEEEEE] w-full overflow-x-auto">
                            <QueryCard query={query} />
                          </div>

                          {/* Add New Feedback Section */}

                          {!query.is_closed &&
                            userRole?.code === ROLES.TEACHER && (
                              <QueryFeedbackForm
                                query={query}
                                onRefresh={fetchQueries}
                              />
                            )}
                        </>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div
                  className={`flex flex-col mb-2 justify-center items-center h-1/2`}
                >
                  {userRole?.code === ROLES.SUPER_ADMIN && (
                    <>
                      <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
                        No Queries added by student yet.
                      </p>
                    </>
                  )}

                  {userRole?.code === ROLES.STUDENT && (
                    <>
                      <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
                        No Queries created yet.
                      </p>

                      <div>
                        <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
                          Create a query submission to share your queries here.
                        </p>
                      </div>
                    </>
                  )}

                  {userRole?.code === ROLES.TEACHER && (
                    <>
                      <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
                        No Queries added by student yet
                      </p>

                      <div>
                        <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
                          Your queries from your student will be listed here.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              className={`flex flex-col mb-2 justify-center items-center h-1/2`}
            >
              <p className="mt-4 text-gray-600">Loading Queries...</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Queries;
